import React, {Component} from 'react';
import {findDOMNode} from 'react-dom';
import t from 'tcomb-form';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import {DragSource, DropTarget, DragDropContext} from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import {Glyphicon, Dropdown, MenuItem} from 'react-bootstrap';
import update from 'immutability-helper';

const ItemTypes = {
    GUIDELINE: 'guideline'
};

const guidelineSource = {
    beginDrag(props) {
        return {
            guideline: props.guideline,
            index: props.index
        };
    }
};

const guidelineTarget = {
    hover(props, monitor, component) {
        const ownGuideline = props.guideline.guideline;
        const draggedGuideline = monitor.getItem().guideline.guideline;
        if (draggedGuideline === ownGuideline) {
            return;
        }

        const ownIndex = props.index;
        const draggedIndex = monitor.getItem().index;

        // What is my rectangle on screen?
        // @ts-ignore
        const boundingRect = findDOMNode(component).getBoundingClientRect();
        // Where is the mouse right now?
        const clientOffset = monitor.getClientOffset();
        // Where is my vertical middle?
        const ownMiddleY = (boundingRect.bottom - boundingRect.top) / 2;
        // How many pixels to the top?
        const offsetY = clientOffset.y - boundingRect.top;

        // We only want to move when the mouse has crossed half of the item's height.
        // If we're dragging down, we want to move only if we're below 50%.
        // If we're dragging up, we want to move only if we're above 50%.

        // Moving down: exit if we're in upper half
        if (draggedIndex < ownIndex && offsetY < ownMiddleY) {
            return;
        }

        // Moving up: exit if we're in lower half
        if (draggedIndex > ownIndex && offsetY > ownMiddleY) {
            return;
        }

        // Time to actually perform the action!
        props.onMoveGuideline(draggedGuideline, ownGuideline);
    }
};

@DropTarget(ItemTypes.GUIDELINE, guidelineTarget, connect => ({
    connectDropTarget: connect.dropTarget()
}))
@DragSource(ItemTypes.GUIDELINE, guidelineSource, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging()
}))
class FormCampaignGuideline extends Component {
    static displayName = 'FormCampaignGuideline';
    static propTypes = {
        connectDragSource: PropTypes.func.isRequired,
        connectDropTarget: PropTypes.func.isRequired,
        index: PropTypes.number.isRequired,
        isDragging: PropTypes.bool.isRequired,
        onRemoveItem: PropTypes.func.isRequired,
        onEditItem: PropTypes.func.isRequired,
        guideline: PropTypes.shape({
            guideline: PropTypes.string.isRequired,
            should: PropTypes.number.isRequired
        }).isRequired
    };

    render() {
        const {
            isDragging,
            connectDragSource,
            connectDropTarget,
            guideline,
            index,
            onRemoveItem,
            onEditItem
        } = this.props;

        const opacity = isDragging ? 0 : 1;
        const glyph = guideline.should ? 'plus-sign' : 'minus-sign';
        const glyphStyle = {
            color: guideline.should ? '#7ABB36' : '#E74A3C'
        };

        return connectDragSource(
            connectDropTarget(
                <div
                    key={index}
                    className="list-group-item list-group-item-neutral"
                    style={{opacity}}
                >
                    <div className="list-group-item-guideline">
                        <Glyphicon style={glyphStyle} glyph={glyph} />
                        <div>{guideline.guideline}</div>
                    </div>
                    <span onClick={e => onEditItem(e, guideline)}>
                        &#x270e;
                    </span>
                    <span onClick={e => onRemoveItem(e, guideline.guideline)}>
                        x
                    </span>
                    <br />
                </div>
            )
        );
    }
}

@DragDropContext(HTML5Backend)
class CampaignGuidelinesGroup extends Component {
    static displayName = 'CampaignGuidelinesGroup';
    static propTypes = {
        guidelines: PropTypes.array.isRequired,
        onAddItem: PropTypes.func.isRequired,
        onMoveGuideline: PropTypes.func.isRequired,
        onRemoveItem: PropTypes.func.isRequired
    };

    constructor() {
        super();
        this.state = {
            guidelineType: 'MUST'
        };
    }

    handleGuidelineTypeSelect = guidelineType => {
        this.setState({
            guidelineType
        });
    };

    handleAddClick = (e) => {
        if (e) e.preventDefault();
        const {guidelineType} = this.state;

        const input = findDOMNode(this.refs.guidelineInput);
        const guideline = input.value;
        const shouldGuideline = guidelineType === 'MUST' ? 1 : 0;

        if (this.props.onAddItem(guideline, shouldGuideline)) {
            input.value = '';
            input.focus();
        }
    };

    handleInputKeyUp = (e) => {
        e.preventDefault();

        if (e.keyCode === 13) {
            this.handleAddClick();
        }
    };

    handleEditItem = (e, guideline) => {
        if (e) e.preventDefault();

        // sets input text to edit value
        const input = findDOMNode(this.refs.guidelineInput);
        input.value = guideline.guideline;

        // sets dropdown guidelines type to edit value
        this.setState({
            guidelineType: guideline.should ? 'MUST' : 'MUST NOT'
        });

        // invoke the removal
        this.props.onRemoveItem(e, guideline.guideline);
    };

    displayGuidelinesTypeDropdown = () => {
        const {guidelineType} = this.state;

        const isMustGuideline = guidelineType === 'MUST';
        const dropdownItemText = isMustGuideline ? 'MUST NOT' : 'MUST';
        const dropdownSelectedClass = classNames({
            'guideline-must': isMustGuideline,
            'guideline-must-not': !isMustGuideline
        });
        const dropdownItemClass = classNames({
            'guideline-must': !isMustGuideline,
            'guideline-must-not': isMustGuideline
        });

        return (
            <Dropdown id="campaign-guidelines-type-dropdown">
                <Dropdown.Toggle>
                    Content <span className={dropdownSelectedClass}>{guidelineType}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="guidelines-dropdown-menu">
                    <MenuItem
                        className="guidelines-menu-item"
                        onClick={() => this.handleGuidelineTypeSelect(dropdownItemText)}
                    >
                        Content <span className={dropdownItemClass}>{dropdownItemText}</span>
                    </MenuItem>
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    displayGuidelinesInput = () => {
        const {guidelineType} = this.state;

        const placeholder =
            guidelineType === 'MUST'
                ? 'E.g. Content must include the brand product'
                : 'E.g. Content must not show any birds';

        return (
            <textarea
                className="form-control campaign-guidelines-input"
                ref="guidelineInput"
                onKeyUp={e => this.handleInputKeyUp(e)}
                placeholder={placeholder}
            />
        );
    };

    displayGuidelines = () => {
        const {guidelines, onRemoveItem, onMoveGuideline} = this.props;

        return guidelines.map((g, i) => (
            <FormCampaignGuideline
                guideline={g}
                index={i}
                onEditItem={this.handleEditItem}
                onMoveGuideline={onMoveGuideline}
                key={g.guideline}
                onRemoveItem={onRemoveItem}
            />
        ));
    };

    displayGuidelineLimitWarning = () => {
        const {guidelines} = this.props;
        const isOverLimit = guidelines.length > 9;

        return isOverLimit ? (
            <div>
                <div className="over-limit-warning">
                    <b>
                        Historically, briefs with up to 7 guidelines earn the highest opted in rate,
                        best creators & ultimately the best quality content. We strongly recommend
                        limiting your volume of guidelines to a maximum of 10.
                    </b>
                </div>
            </div>
        ) : null;
    };

    render() {
        const guidelinesTypeDropdown = this.displayGuidelinesTypeDropdown();
        const guidelinesInput = this.displayGuidelinesInput();
        const guidelines = this.displayGuidelines();
        const guidelinesLimitWarning = this.displayGuidelineLimitWarning();

        return (
            <div className="form-campaign-guidelines-group">
                <div className="input-group">
                    {guidelinesTypeDropdown}
                    {guidelinesInput}
                    <span className="input-group-btn">
                        <button className="btn btn-success btn-small" onClick={this.handleAddClick}>
                            Add
                        </button>
                    </span>
                </div>
                <div className="list-group">
                    {guidelines}
                    {guidelinesLimitWarning}
                </div>
            </div>
        );
    }
}
export default class FormCampaignGuidelines extends Component {
    static displayName = 'FormCampaignGuidelines';

    constructor(props) {
        super(props);
        this.state = {
            value: props.value || [],
            id: props.ctx.uidGenerator.next()
        };
    }

    handleAddItem = (guideline, should) => {
        if (!guideline) return false;

        // Duplicate guideline -- don't allow it to be added
        if (R.any(R.propEq('guideline', guideline), this.state.value)) {
            return false;
        }
        const value = this.state.value.concat({guideline, should, type: 'image'});

        this.setState({value}, () => {
            this.props.onChange(value, this.props.ctx.path.concat(value.length - 1), 'add');
        });

        return true;
    };

    handleRemoveItem = (e, guideline) => {
        e.preventDefault();
        const value = this.state.value.slice();
        const index = R.findIndex(R.propEq('guideline', guideline), value);
        value.splice(index, 1);
        this.setState({value}, () => {
            this.props.onChange(value, this.props.ctx.path.concat(index), 'remove');
        });
    };

    handleMoveGuideline = (guideline, afterGuideline) => {
        const guidelines = this.state.value;
        const g = guidelines.filter(g => g.guideline === guideline)[0];
        const afterG = guidelines.filter(g => g.guideline === afterGuideline)[0];
        const guidelineIndex = guidelines.indexOf(g);
        const afterIndex = guidelines.indexOf(afterG);

        this.setState(
            update(this.state, {
                value: {
                    $splice: [[guidelineIndex, 1], [afterIndex, 0, g]]
                }
            }),
            () => {
                this.props.onChange(this.state.value, this.props.ctx.path);
            }
        );
    };

    getValidationOptions() {
        return {
            path: this.props.ctx.path,
            context: t.mixin(t.mixin({}, this.props.context || this.props.ctx.context), {
                options: this.props.options
            })
        };
    }

    validate() {
        const value = this.state.value;
        const result = t.validate(value, this.props.type, this.getValidationOptions());
        this.setState({hasError: !result.isValid()});
        return result;
    }

    render() {
        const label = this.props.options.label || this.props.ctx.label;
        const name = this.props.options.name || '';
        const {hasError, value} = this.state;
        const formCampaignGuidelinesClasses = classNames(
            'form-group',
            hasError && 'has-error',
            'form-campaign-guidelines'
        );

        return (
            <div className={formCampaignGuidelinesClasses}>
                <label className="label-large">{label}</label>
                <CampaignGuidelinesGroup
                    name={name}
                    guidelines={value}
                    onAddItem={this.handleAddItem}
                    onMoveGuideline={this.handleMoveGuideline}
                    onRemoveItem={this.handleRemoveItem}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./form_components/form_campaign_guidelines.js